import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Avatar from "../common/Avatar";
import ConfirmationModal from "../club/ConfirmationModal";
import useTranslate from "../../hooks/useTranslate";
import { loungeService } from "../../services/loungeService";
import { ChannelMenuDesktop, ChannelSheetMobile } from "./ChannelPicker";
import { formatFileSize } from "../inbox/sharedContent";

// Composer — frames 1b (expanded), 1d (empty-feed variant), 1e (mobile),
// 1i (validation, limit, published toast), 1j (channel selector states),
// 1k (mobile channel bottom sheet), 1p (character limit).
//
// Cancel with drafted text OR attachments asks via the platform's
// ConfirmationModal (variant v2 — centered dialog on mobile too, per 1i/1k).
// T6 moved the channel dropdown/sheet markup into ChannelPicker.js (shared
// with the inline post editor, frame 1m·2) — same markup, same tokens.
//
// T7 attachments (contract §1 mirrored client-side, GATE0 §5): up to 3
// images (jpeg/png/webp) OR one PDF — never mixed, each ≤10 MB, MIME and
// extension both checked. The paperclip (1b: 34px desktop / 1k: 44px
// mobile) opens the picker; chips per 1b (image 96×72 with local object
// URL, PDF 34px tile), × removes any time. Violations show as a plain hint
// line in the composer's hint style — no new error color exists in the
// design.

const MAX_LEN = 1000;

// T7 client mirror of the server rules (same numbers, same wording family).
const MAX_IMAGES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const IMAGE_EXTENSIONS = {
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/webp": ["webp"],
};
const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

const PaperclipIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className="w-4 h-4 sm:w-[15px] sm:h-[15px]"
  >
    <path
      d="M13 7.5L8.2 12.3a3 3 0 01-4.2-4.2L9 3a2 2 0 012.8 2.8L7 10.6a1 1 0 01-1.4-1.4l4.3-4.3"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Chip × — 1b: 18px disc on rgba ground, top-right.
const RemoveChipButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="absolute top-1 right-1 w-[18px] h-[18px] rounded-full bg-[rgba(17,24,39,0.65)] text-white border-none text-[11px] leading-none flex items-center justify-center cursor-pointer"
  >
    ×
  </button>
);

export const CaretDown = ({ color = "#6B7280" }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 6l4.5 4.5L12.5 6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const CaretUp = ({ color = "#2283FF" }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 10l4.5-4.5L12.5 10" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LoungeComposer = ({ channels, currentUser, onCreated, forceExpanded = false }) => {
  const { t, tv } = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState("");
  const [channelSlug, setChannelSlug] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // T7: [{key, kind: "image"|"pdf", file, url}] — url = local object URL
  // for image chips (revoked on remove/reset), null for the PDF chip.
  const [attachments, setAttachments] = useState([]);
  // T7: one of null | "limit" | "size" | "type" — rendered as a hint line.
  const [attachError, setAttachError] = useState(null);
  const textareaRef = useRef(null);
  const pickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachKey = useRef(0);

  const isOpen = expanded || forceExpanded;
  const selected = channels.find((c) => c.slug === channelSlug) || null;
  const atLimit = text.length >= MAX_LEN;
  const needsChannel = text.trim().length > 0 && !selected;
  const canPost = text.trim().length > 0 && Boolean(selected) && !atLimit && !submitting;

  // T7 picker gating (workorder): with images picked, only images up to 3
  // may follow; with the PDF picked, nothing may follow.
  const imageCount = attachments.filter((a) => a.kind === "image").length;
  const hasPdf = attachments.some((a) => a.kind === "pdf");
  const attachDisabled = hasPdf || imageCount >= MAX_IMAGES;
  const attachAccept =
    imageCount > 0 ? IMAGE_ACCEPT : `${IMAGE_ACCEPT},application/pdf`;

  useEffect(() => {
    if (expanded && textareaRef.current) textareaRef.current.focus();
  }, [expanded]);

  // Close the desktop dropdown on outside click (the mobile sheet has its own
  // overlay + cancel button).
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pickerOpen]);

  // T7: revoke the local image previews if the composer unmounts with
  // chips still drafted (route change) — reset() covers the normal paths.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(
    () => () =>
      attachmentsRef.current.forEach((a) => a.url && URL.revokeObjectURL(a.url)),
    [],
  );

  const reset = () => {
    setText("");
    setChannelSlug(null);
    setPickerOpen(false);
    setExpanded(false);
    attachments.forEach((a) => a.url && URL.revokeObjectURL(a.url));
    setAttachments([]);
    setAttachError(null);
  };

  // T7: run every picked file through the client mirror of the server
  // rules; accepted files become chips, the first violation becomes the
  // hint line (files after a violation in the same pick still get their
  // chance — order-independent, like the server).
  const addFiles = (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.length === 0) return;
    let next = attachments;
    let error = null;
    picked.forEach((file) => {
      const isPdf = file.type === "application/pdf";
      const isImage = Boolean(IMAGE_EXTENSIONS[file.type]);
      const ext = (String(file.name).match(/\.([a-z0-9]+)$/i)?.[1] || "").toLowerCase();
      const extOk = isPdf
        ? ext === "pdf"
        : (IMAGE_EXTENSIONS[file.type] || []).includes(ext);
      if ((!isPdf && !isImage) || !extOk) {
        error = error || "type";
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        error = error || "size";
        return;
      }
      const pdfInNext = next.some((a) => a.kind === "pdf");
      const imagesInNext = next.filter((a) => a.kind === "image").length;
      if (
        (isPdf && next.length > 0) ||
        (!isPdf && pdfInNext) ||
        (!isPdf && imagesInNext >= MAX_IMAGES)
      ) {
        error = error || "limit";
        return;
      }
      next = [
        ...next,
        {
          key: (attachKey.current += 1),
          kind: isPdf ? "pdf" : "image",
          file,
          url: isPdf ? null : URL.createObjectURL(file),
        },
      ];
    });
    setAttachments(next);
    setAttachError(error);
  };

  const removeAttachment = (key) => {
    const gone = attachments.find((a) => a.key === key);
    if (gone?.url) URL.revokeObjectURL(gone.url);
    setAttachments(attachments.filter((a) => a.key !== key));
    setAttachError(null);
  };

  const handleCancel = () => {
    if (text.trim() || attachments.length > 0) {
      setDiscardOpen(true);
    } else {
      reset();
    }
  };

  const handlePost = async () => {
    if (!canPost) return;
    setSubmitting(true);
    const res = await loungeService.createPost({
      text: text.trim(),
      channel: selected.slug,
      files: attachments.map((a) => a.file),
    });
    setSubmitting(false);
    if (res.success) {
      // Published toast — 1i: dark bar, white check + "Posted to {channel}".
      toast(
        <span className="flex items-center gap-2.5">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3.5 8.5l3 3 6-6.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="text-[13px] font-semibold text-white">
            {tv("Posted to {{channel}}", { channel: selected.name })}
          </span>
        </span>,
        {
          style: {
            background: "#111827",
            borderRadius: 12,
            padding: "12px 16px",
            boxShadow: "0 12px 32px rgba(17,24,39,0.18)",
          },
        },
      );
      reset();
      if (onCreated) onCreated(res.post);
    } else {
      toast.error(res.error || t("Something went wrong. Please try again."));
    }
  };

  const avatar = (size) => (
    <div className="shrink-0">
      <Avatar
        src={currentUser?.profile_picture_url || currentUser?.profile_picture}
        alt={currentUser?.name || ""}
        size={size}
        fallbackText={currentUser?.name}
      />
    </div>
  );

  // ---- Channel picker pieces -------------------------------------------

  const triggerLabel = selected ? (
    <span className="inline-flex items-center gap-[7px] text-[#111827] font-medium">
      <span className="w-[7px] h-[7px] rounded-full bg-[#2283FF] shrink-0" />
      {selected.name}
    </span>
  ) : (
    t("Choose a channel")
  );

  const triggerEmphasis = needsChannel
    ? "border-2 border-[#2283FF] bg-[#F4F8FF] font-medium text-[#111827]"
    : pickerOpen
      ? "border border-[#2283FF] bg-white shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
      : "border border-[#DDE3EB] bg-white hover:border-[#C6CFDA]";

  const desktopMenu = pickerOpen && (
    <ChannelMenuDesktop
      channels={channels}
      value={channelSlug}
      onSelect={(slug) => {
        setChannelSlug(slug);
        setPickerOpen(false);
      }}
    />
  );

  const mobileSheet = pickerOpen && (
    <ChannelSheetMobile
      channels={channels}
      value={channelSlug}
      onSelect={(slug) => {
        setChannelSlug(slug);
        setPickerOpen(false);
      }}
      onClose={() => setPickerOpen(false)}
    />
  );

  // ---- Collapsed trigger (1a/1e) ---------------------------------------

  if (!isOpen) {
    return (
      <>
        <div
          className="mt-3 sm:mt-4 bg-white border border-[#E4E9F0] rounded-[14px] p-[9px_11px] min-[402px]:p-[10px_12px] sm:p-[12px_14px] flex items-center gap-[9px] min-[402px]:gap-2.5 sm:gap-3 cursor-pointer hover:border-[#C6CFDA]"
          onClick={() => setExpanded(true)}
          data-testid="composer-trigger"
        >
          {avatar(34)}
          <div className="flex-1 border border-[#DDE3EB] rounded-full py-[11px] px-3.5 sm:py-[9px] sm:px-4 text-[13px] sm:text-[13.5px] text-[#9CA3AF] select-none">
            {t("Share with the lounge…")}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
            className="hidden sm:inline-flex bg-[#2283FF] text-white border-none rounded-[10px] py-[9px] px-[18px] text-[13.5px] font-semibold whitespace-nowrap shrink-0 hover:bg-[#1B6FE0]"
          >
            {t("New post")}
          </button>
        </div>
        {/* Mobile FAB — 52px, bottom-right (1e/1l). */}
        <button
          type="button"
          aria-label={t("New post")}
          onClick={() => setExpanded(true)}
          className="sm:hidden fixed bottom-[18px] right-3.5 min-[402px]:bottom-5 min-[402px]:right-4 w-[52px] h-[52px] rounded-full bg-[#2283FF] border-none shadow-[0_6px_16px_rgba(34,131,255,0.35)] flex items-center justify-center z-40"
          data-testid="composer-fab"
        >
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M2 3.5A1.5 1.5 0 013.5 2h9A1.5 1.5 0 0114 3.5v6a1.5 1.5 0 01-1.5 1.5H8l-3 2.5V11H3.5A1.5 1.5 0 012 9.5z"
              fill="#fff"
            />
          </svg>
        </button>
      </>
    );
  }

  // ---- Expanded card (1b desktop / 1e mobile / 1d empty variant) --------

  return (
    <div
      className="mt-3.5 sm:mt-[18px] bg-white border border-[#E4E9F0] rounded-[14px] p-[14px_15px] sm:p-[16px_18px] flex gap-2.5 sm:gap-3"
      data-testid="composer-expanded"
    >
      <div className="hidden sm:block shrink-0">{avatar(38)}</div>
      <div className="flex-1 min-w-0 flex flex-col gap-[11px] sm:gap-3">
        <div className="flex gap-2.5 sm:block">
          <div className="sm:hidden">{avatar(34)}</div>
          <textarea
            ref={textareaRef}
            value={text}
            maxLength={MAX_LEN}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            placeholder={t(
              "Share with the lounge… (no club names, no player names, no active deals)",
            )}
            className="flex-1 w-full min-h-[96px] sm:min-h-[74px] border border-[#DDE3EB] rounded-[10px] py-2.5 px-3 text-[14px] leading-[1.55] text-[#111827] placeholder-[#9CA3AF] resize-y outline-none focus:border-[#2283FF] focus:shadow-[0_0_0_3px_rgba(34,131,255,0.14)]"
            data-testid="composer-textarea"
          />
        </div>
        <p className="m-0 -mt-[3px] sm:-mt-1 ml-11 sm:ml-0 text-[11.5px] sm:text-[12px] text-[#9CA3AF]">
          {t("No club names, no player names, no active deals.")}
        </p>

        {/* T7 chips row (1b): image chip 96×72 with local preview, PDF chip
            with 34px tile — × removes any time. */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2.5" data-testid="composer-chips">
            {attachments.map((a) =>
              a.kind === "image" ? (
                <div
                  key={a.key}
                  className="relative w-24 h-[72px] border border-[#DDE3EB] rounded-[10px] overflow-hidden"
                  data-testid="chip-image"
                >
                  <img
                    src={a.url}
                    alt={a.file.name}
                    className="block w-full h-full object-cover"
                  />
                  <RemoveChipButton
                    onClick={() => removeAttachment(a.key)}
                    label={tv("Remove {{name}}", { name: a.file.name })}
                  />
                </div>
              ) : (
                <div
                  key={a.key}
                  className="relative flex items-center gap-2.5 border border-[#DDE3EB] rounded-[10px] p-[10px_30px_10px_12px] bg-white max-w-full"
                  data-testid="chip-pdf"
                >
                  <span className="w-[34px] h-[34px] rounded-[8px] bg-[#EDF4FF] text-[#2283FF] flex items-center justify-center text-[10px] font-bold shrink-0">
                    PDF
                  </span>
                  <span className="flex flex-col min-w-0">
                    <span className="text-[13px] font-semibold text-[#111827] whitespace-nowrap overflow-hidden text-ellipsis">
                      {a.file.name}
                    </span>
                    <span className="text-[12px] text-[#9CA3AF]">
                      {formatFileSize(a.file.size)}
                    </span>
                  </span>
                  <RemoveChipButton
                    onClick={() => removeAttachment(a.key)}
                    label={tv("Remove {{name}}", { name: a.file.name })}
                  />
                </div>
              ),
            )}
          </div>
        )}

        {/* Mobile: channel selector as its own full-width row (1e/1k). */}
        <button
          type="button"
          onClick={() => setPickerOpen((o) => !o)}
          className={`sm:hidden flex items-center justify-between rounded-[10px] py-[11px] px-3 min-h-[44px] text-[13px] text-left ${
            selected || needsChannel ? "text-[#111827]" : "text-[#6B7280]"
          } ${triggerEmphasis}`}
          data-testid="channel-trigger-mobile"
        >
          {triggerLabel}
          {pickerOpen ? <CaretUp /> : <CaretDown color={needsChannel ? "#2283FF" : "#6B7280"} />}
        </button>

        {selected && (
          <p className="m-0 -mt-[3px] sm:hidden text-[12px] text-[#6B7280]">
            <span className="font-semibold text-[#111827]">{selected.name}:</span>{" "}
            {selected.description}
          </p>
        )}

        <div className="flex items-center gap-2.5 sm:gap-3">
          {/* T7 attach (1b: 34px desktop / 1k: 44px mobile) — the picker
              accept narrows with the current mix, the button locks at 3
              images or one PDF. */}
          <input
            ref={fileInputRef}
            type="file"
            accept={attachAccept}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
            data-testid="composer-file-input"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={attachDisabled}
            aria-label={t("Attach files")}
            className={`flex items-center justify-center w-11 h-11 sm:w-[34px] sm:h-[34px] border border-[#DDE3EB] rounded-[10px] bg-white text-[#6B7280] shrink-0 ${
              attachDisabled
                ? "opacity-50 cursor-default"
                : "hover:border-[#C6CFDA] hover:text-[#111827] cursor-pointer"
            }`}
            data-testid="composer-attach"
          >
            <PaperclipIcon />
          </button>

          {/* Mobile counter sits next to the clip (1k). */}
          <span
            className={`sm:hidden ${
              atLimit
                ? "text-[12.5px] font-bold text-[#111827]"
                : "text-[12.5px] text-[#9CA3AF]"
            }`}
            data-testid="composer-counter-mobile"
          >
            {text.length} / {MAX_LEN}
          </span>

          {/* Desktop: channel selector inline in the controls row (1b/1j). */}
          <div ref={pickerRef} className="relative hidden sm:block">
            <button
              type="button"
              onClick={() => setPickerOpen((o) => !o)}
              className={`inline-flex items-center gap-2 rounded-[10px] py-[7px] px-3 text-[13px] whitespace-nowrap ${
                selected || needsChannel ? "text-[#111827]" : "text-[#6B7280]"
              } ${triggerEmphasis}`}
              data-testid="channel-trigger"
            >
              {triggerLabel}
              {pickerOpen ? <CaretUp /> : <CaretDown color={needsChannel ? "#2283FF" : "#6B7280"} />}
            </button>
            {desktopMenu}
          </div>

          <span className="flex-1" />
          <span
            className={`hidden sm:inline ${
              atLimit
                ? "text-[12.5px] font-bold text-[#111827]"
                : "text-[12.5px] text-[#9CA3AF]"
            }`}
            data-testid="composer-counter"
          >
            {text.length} / {MAX_LEN}
          </span>
          {!forceExpanded && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-white border border-[#DDE3EB] rounded-[10px] py-0 px-4 h-11 sm:h-auto sm:py-[9px] text-[13.5px] font-semibold text-[#6B7280] hover:border-[#C6CFDA] hover:text-[#111827] whitespace-nowrap"
            >
              {t("Cancel")}
            </button>
          )}
          <button
            type="button"
            onClick={handlePost}
            disabled={!canPost}
            className={`bg-[#2283FF] text-white border-none rounded-[10px] py-0 px-5 h-11 sm:h-auto sm:py-[9px] text-[13.5px] font-semibold whitespace-nowrap ${
              canPost ? "hover:bg-[#1B6FE0]" : "opacity-50 cursor-default"
            }`}
            data-testid="composer-post"
          >
            {t("Post")}
          </button>
        </div>

        {/* T7 violation hint — plain composer hint style, no new color. */}
        {attachError && (
          <p className="m-0 -mt-1 text-[12.5px] text-[#6B7280]" data-testid="attach-error">
            {attachError === "size"
              ? t("Maximum size is 10MB.")
              : attachError === "type"
                ? t("Only JPG, PNG or WEBP images, or one PDF.")
                : t("Up to 3 images or one PDF per post.")}
          </p>
        )}
        {atLimit && (
          <p className="m-0 -mt-1 text-[12.5px] text-[#6B7280]">
            {t("Limit reached, trim your text to post. 1000 characters max.")}
          </p>
        )}
        {needsChannel && (
          <p className="m-0 -mt-1 text-[12.5px] text-[#6B7280]">
            {t("Pick a channel to post, every discussion lives in one channel.")}
          </p>
        )}
        {selected && (
          <p className="m-0 -mt-0.5 hidden sm:block text-[12.5px] text-[#6B7280]">
            <span className="font-semibold text-[#111827]">{selected.name}:</span>{" "}
            {selected.description}
          </p>
        )}
      </div>

      {mobileSheet}

      <ConfirmationModal
        isOpen={discardOpen}
        variant="v2"
        title={t("Discard this post?")}
        lines={[t("Your text and attachments won't be saved.")]}
        cancelText={t("Discard")}
        confirmText={t("Keep writing")}
        confirmButtonClass="bg-[#2283FF] hover:bg-[#1B6FE0]"
        onClose={() => {
          // Design 1i: the white button DISCARDS, the blue primary keeps
          // writing — so closing the dialog via "Discard" resets the draft.
          setDiscardOpen(false);
          reset();
        }}
        onConfirm={() => setDiscardOpen(false)}
      />
    </div>
  );
};

export default LoungeComposer;
